import React, { useMemo, useState, useRef } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { EVENT_TYPE_COLORS } from '../../constants/notificationTypes';
import { FaPlus } from 'react-icons/fa';

const WeekView = ({
  currentWeek,
  eventi,
  selectedDate,
  onDateSelect,
  onEventSelect,
  onQuickCreate,
  onTimeSlotClick,
  onEventMove,
  navigateDate
}) => {
  const { theme } = useTheme();
  
  // ✅ NEW: Stato per drag & drop con auto-scroll e week navigation
  const [draggedEvent, setDraggedEvent] = useState(null);
  const [dragOverCell, setDragOverCell] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [weekNavigationTriggered, setWeekNavigationTriggered] = useState(false);
  const scrollContainerRef = useRef(null);
  const autoScrollIntervalRef = useRef(null);
  const weekNavigationTimeoutRef = useRef(null);
  const [expandedAllDay, setExpandedAllDay] = useState({}); // ✅ memorizza giorni espansi

  // ✅ NEW: ref per il contenitore scrollabile verticale (griglia oraria)
  const scrollYContainerRef = useRef(null);

  // Orari coperti: 7-23
  const orariGiorno = useMemo(() => {
    const orari = [];
    for (let hour = 7; hour <= 23; hour++) {
      orari.push(hour);
    }
    return orari;
  }, []);

  // Group events by day and hour
  const eventiPerGiornoOra = useMemo(() => {
    const map = {};
    
    currentWeek.forEach(date => {
      const dateKey = date.toDateString();
      map[dateKey] = {};
      
      // ✅ AGGIUNTO: Slot speciale per eventi di giornata intera
      map[dateKey]['all-day'] = [];
      
      orariGiorno.forEach(hour => {
        map[dateKey][hour] = [];
      });
    });

    eventi.forEach(evento => {
      const startDate = new Date(evento.CAL_DataOraInizio);
      if (!isNaN(startDate.getTime())) {
        const dateKey = startDate.toDateString();
        const hour = startDate.getHours();
        
        // ✅ NUOVO: Gestione eventi giornata intera
        if (evento.CAL_GiornataIntera === 1 || evento.CAL_GiornataIntera === true) {
          if (map[dateKey]) {
            map[dateKey]['all-day'].push(evento);
          }
        }
        // Eventi con orario specifico
        else if (map[dateKey] && map[dateKey][hour] !== undefined) {
          map[dateKey][hour].push(evento);
        }
        // ✅ FALLBACK: Eventi fuori range orario (0-6, 24+) vanno in slot all-day
        else if (map[dateKey] && (hour < 7 || hour > 23)) {
          map[dateKey]['all-day'].push(evento);
        }
      }
    });

    return map;
  }, [currentWeek, eventi, orariGiorno]);

  // ✅ CRITICAL FIX: Week navigation che non interrompe il drag
  const handleWeekNavigation = (direction) => {
    if (weekNavigationTriggered) return;
    setWeekNavigationTriggered(true);

    if (navigateDate) {
      navigateDate(direction);
    }

    // NON azzerare il drag; consenti il drop nella nuova settimana
    setDragOverCell(null);

    // ✅ CRITICAL: Reset del lock più veloce per permettere nuova navigazione
    weekNavigationTimeoutRef.current = setTimeout(() => {
      setWeekNavigationTriggered(false);
    }, 300); // Ridotto da 600 a 300ms
  };

  // ✅ Consenti il drop anche subito dopo la navigazione
  const handleDrop = (e, targetDate, targetHour) => {
    e.preventDefault();
    if (!draggedEvent) return;

    // ✅ CRITICAL: Calcola la data corretta considerando la settimana attuale
    const originalDate = new Date(draggedEvent.CAL_DataOraInizio);
    const newDate = new Date(targetDate);
    
    // ✅ IMPORTANT: Mantieni la data della settimana corrente, non quella originale
    newDate.setHours(targetHour, originalDate.getMinutes(), originalDate.getSeconds(), originalDate.getMilliseconds());

    let newEndDate = null;
    if (draggedEvent.CAL_DataOraFine) {
      const originalEndDate = new Date(draggedEvent.CAL_DataOraFine);
      const duration = originalEndDate.getTime() - originalDate.getTime();
      newEndDate = new Date(newDate.getTime() + duration);
    }

    onEventMove?.(draggedEvent, {
      newStartDate: newDate,
      newEndDate,
      originalStartDate: originalDate,
      targetDate,
      targetHour
    });

    setDraggedEvent(null);
    setDragOverCell(null);
    setIsDragging(false);
    setWeekNavigationTriggered(false); // ✅ Reset anche qui
  };

  // ✅ IMPROVED: Auto-scroll + week navigation ai bordi
  const handleAutoScroll = (e) => {
    if (!scrollContainerRef.current || !isDragging) return;

    const outer = scrollContainerRef.current;
    const rect = outer.getBoundingClientRect();
    const weekNavigationThreshold = 60; // ✅ Ridotto per facilitare il trigger
    const scrollThreshold = 50;
    const scrollSpeed = 8;

    const mouseY = e.clientY - rect.top;
    const mouseX = e.clientX - rect.left;
    const containerHeight = rect.height;
    const containerWidth = rect.width;



    // Navigazione settimana ai bordi
    if (mouseX < weekNavigationThreshold && !weekNavigationTriggered) {

      handleWeekNavigation(-1);
      return;
    } else if (mouseX > containerWidth - weekNavigationThreshold && !weekNavigationTriggered) {

      handleWeekNavigation(1);
      return;
    }

    // ✅ Rest of vertical scroll logic...
    // Stop previous auto-scroll
    if (autoScrollIntervalRef.current) {
      clearInterval(autoScrollIntervalRef.current);
      autoScrollIntervalRef.current = null;
    }

    const scrollable = scrollYContainerRef.current;
    if (!scrollable) return;

    if (mouseY < scrollThreshold && scrollable.scrollTop > 0) {
      autoScrollIntervalRef.current = setInterval(() => {
        if (scrollable.scrollTop <= 0) {
          clearInterval(autoScrollIntervalRef.current);
          autoScrollIntervalRef.current = null;
          return;
        }
        scrollable.scrollTop = Math.max(0, scrollable.scrollTop - scrollSpeed);
      }, 16);
    } else if (
      mouseY > containerHeight - scrollThreshold &&
      scrollable.scrollTop < scrollable.scrollHeight - scrollable.clientHeight
    ) {
      autoScrollIntervalRef.current = setInterval(() => {
        if (scrollable.scrollTop >= scrollable.scrollHeight - scrollable.clientHeight) {
          clearInterval(autoScrollIntervalRef.current);
          autoScrollIntervalRef.current = null;
          return;
        }
        scrollable.scrollTop = Math.min(
          scrollable.scrollHeight - scrollable.clientHeight,
          scrollable.scrollTop + scrollSpeed
        );
      }, 16);
    }
  };

  // ✅ IMPROVED: Drag handlers con week navigation
  const handleDragStart = (e, evento) => {
    setDraggedEvent(evento);
    setIsDragging(true);
    setWeekNavigationTriggered(false);
    
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', evento.CAL_Id_CalendarioEventi);
    e.target.style.opacity = '0.5';

    // ✅ REMOVED: No longer need document listener since we call handleAutoScroll in dragOver
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedEvent(null);
    setDragOverCell(null);
    setIsDragging(false);
    setWeekNavigationTriggered(false);

    // Cleanup completo
    // ✅ REMOVED: No longer needed
    if (autoScrollIntervalRef.current) {
      clearInterval(autoScrollIntervalRef.current);
      autoScrollIntervalRef.current = null;
    }
    if (weekNavigationTimeoutRef.current) {
      clearTimeout(weekNavigationTimeoutRef.current);
      weekNavigationTimeoutRef.current = null;
    }
  };

  // ✅ CRITICAL FIX: Drop handler che non interferisce con week navigation
  const handleDragOver = (e, date, hour) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    // ✅ CRITICAL: Call auto-scroll here to detect edge navigation
    handleAutoScroll(e);

    const cellKey = `${date.toDateString()}-${hour}`;
    if (dragOverCell !== cellKey) {
      setDragOverCell(cellKey);
    }
  };

  // ✅ Define handleDragLeave to clear highlight safely
  const handleDragLeave = (e) => {
    // if moving into a child, ignore
    const related = e.relatedTarget;
    if (related && e.currentTarget.contains(related)) return;

    setDragOverCell(null);
  };

  // ✅ Render event function
  const renderEvent = (evento) => {
    const colorClass = getEventColor(evento.CAL_Id_TipiEventi);
    const startTime = new Date(evento.CAL_DataOraInizio);
    const endTime = evento.CAL_DataOraFine ? new Date(evento.CAL_DataOraFine) : null;
    const isBeingDragged = draggedEvent?.CAL_Id_CalendarioEventi === evento.CAL_Id_CalendarioEventi;
    
    return (
      <div
        key={evento.CAL_Id_CalendarioEventi}
        draggable={true}
        onDragStart={(e) => handleDragStart(e, evento)}
        onDragEnd={handleDragEnd}
        onClick={(e) => {
          e.stopPropagation();
          if (!isDragging) {
            onEventSelect(evento);
          }
        }}
        className={`
          ${colorClass} text-white text-xs p-1 rounded-md mb-0.5 
          cursor-move hover:opacity-90 hover:scale-[1.02] 
          transition-all duration-200 relative z-10 shadow-sm
          ${isBeingDragged ? 'opacity-50 scale-95' : ''}
          ${isDragging && !isBeingDragged ? 'pointer-events-none' : ''}
        `}
        title={`${evento.CAL_Titolo} - ${formatTime(evento.CAL_DataOraInizio)}${endTime ? ` - ${formatTime(evento.CAL_DataOraFine)}` : ''} (Trascina ai bordi per cambiare settimana)`}
      >
        <div className="font-medium truncate text-xs leading-tight">{evento.CAL_Titolo}</div>
        <div className="text-xs opacity-90 leading-tight">
          {formatTime(evento.CAL_DataOraInizio)}
          {endTime && ` - ${formatTime(evento.CAL_DataOraFine)}`}
        </div>
        
        {/* ✅ NEW: Indicatore drag visivo */}
        {isBeingDragged && (
          <div className="absolute inset-0 bg-white/20 rounded-md flex items-center justify-center">
            <span className="text-xs font-bold">📦</span>
          </div>
        )}
      </div>
    );
  };

  const handleCellClick = (date, hour, isAddButton = false) => {
    if (isDragging) return;
    
    const eventDateTime = new Date(date);
    eventDateTime.setHours(hour, 0, 0, 0);
    
    if (isAddButton) {
      onQuickCreate && onQuickCreate(eventDateTime);
    } else {
      onTimeSlotClick && onTimeSlotClick(eventDateTime, hour);
    }
  };

  const toggleAllDayExpand = (dateKey) => {
    setExpandedAllDay(prev => ({
      ...prev,
      [dateKey]: !prev[dateKey]
    }));
  };

  // ✅ Cleanup
  React.useEffect(() => {
    return () => {
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current);
      }
      if (weekNavigationTimeoutRef.current) {
        clearTimeout(weekNavigationTimeoutRef.current);
      }
      // ✅ REMOVED: No longer using document listener
      // document.removeEventListener('mousemove', handleAutoScroll);
    };
  }, []);

  // Utility functions
  const formatHour = (hour) => {
    return `${hour.toString().padStart(2, '0')}:00`;
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  };

  const getEventColor = (tipoId) => {
    const tipo = EVENT_TYPE_COLORS[tipoId];
    return tipo ? tipo.bg : 'bg-gray-500';
  };

  // Theme styles
  const textColor = theme === 'dark' ? 'text-white' : 'text-gray-900';
  const mutedText = theme === 'dark' ? 'text-gray-400' : 'text-gray-500';
  const headerBg = theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100';
  const cardBg = theme === 'dark' ? 'bg-gray-800' : 'bg-white';
  const cellBorder = theme === 'dark' ? 'border-gray-600' : 'border-gray-200';

  return (
    <div
      ref={scrollContainerRef}
      className={`${cardBg} rounded-lg border shadow-sm overflow-hidden relative`}
    >
      {/* Header con giorni della settimana */}
      <div className={`${headerBg} border-b ${cellBorder}`}>
        <div className="grid grid-cols-8 gap-0">
          <div className={`p-3 border-r ${cellBorder} ${headerBg}`}>
            <span className={`text-sm font-medium ${textColor}`}>Orario</span>
          </div>
          {currentWeek.map((date) => (
            <div
              key={date.toDateString()}
              className={`p-3 border-r last:border-r-0 ${cellBorder} text-center cursor-pointer transition-colors hover:bg-blue-50 dark:hover:bg-blue-900/20`}
              onClick={() => onDateSelect?.(date)}
            >
              <div className={`text-sm font-medium ${textColor}`}>
                {date.toLocaleDateString('it-IT', { weekday: 'short' })}
              </div>
              <div className={`text-lg font-bold ${textColor}`}>
                {date.getDate()}
              </div>
              <div className={`text-xs ${mutedText}`}>
                {date.toLocaleDateString('it-IT', { month: 'short' })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ✅ Sezione Eventi Giornata Intera COMPRESSA */}
      <div className={`${headerBg} border-b ${cellBorder}`}>
        <div className="grid grid-cols-8 gap-0">
          <div className={`p-1.5 border-r ${cellBorder} ${headerBg} flex items-center justify-center`}>
            <span className={`text-[10px] font-medium ${mutedText}`}>Tutto il giorno</span>
          </div>
          {currentWeek.map(date => {
            const dateKey = date.toDateString();
            const allDayEvents = eventiPerGiornoOra[dateKey]?.['all-day'] || [];
            const isExpanded = expandedAllDay[dateKey];
            const MAX_VISIBLE = 3;
            const visible = isExpanded ? allDayEvents : allDayEvents.slice(0, MAX_VISIBLE);
            const hiddenCount = allDayEvents.length - visible.length;

            return (
              <div
                key={`${dateKey}-allday`}
                className={`px-1 py-0.5 border-r last:border-r-0 ${cellBorder} h-9 overflow-hidden`}
              >
                {allDayEvents.length > 0 ? (
                  <div className="flex flex-wrap gap-1 items-center">
                    {visible.map(ev => (
                      <div
                        key={ev.CAL_Id_CalendarioEventi}
                        onClick={(e) => {
                          e.stopPropagation();
                          onEventSelect(ev);
                        }}
                        className={`${getEventColor(ev.CAL_Id_TipiEventi)} text-white text-[9px] leading-none px-1.5 py-0.5 rounded-sm cursor-pointer hover:opacity-90 max-w-[55px] truncate`}
                        title={`${ev.CAL_Titolo} - Giornata intera`}
                      >
                        {ev.CAL_Titolo}
                      </div>
                    ))}
                    {hiddenCount > 0 && !isExpanded && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleAllDayExpand(dateKey);
                        }}
                        className={`text-[9px] px-1 py-0.5 rounded-sm border ${theme === 'dark' ? 'border-gray-500 text-gray-300' : 'border-gray-300 text-gray-600'} hover:bg-gray-200/40 dark:hover:bg-gray-600/40`}
                        title={`Mostra altri ${hiddenCount}`}
                      >
                        +{hiddenCount}
                      </button>
                    )}
                    {isExpanded && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleAllDayExpand(dateKey);
                        }}
                        className={`text-[9px] px-1 py-0.5 rounded-sm border ${theme === 'dark' ? 'border-gray-500 text-gray-300' : 'border-gray-300 text-gray-600'} hover:bg-gray-200/40 dark:hover:bg-gray-600/40`}
                        title="Comprimi"
                      >
                        −
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="h-full flex items-center justify-center group">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onTimeSlotClick?.(date, 0, true);
                      }}
                      className={`opacity-0 group-hover:opacity-100 transition ${theme === 'dark' ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-emerald-600 hover:bg-emerald-700'} text-white p-1 rounded-full`}
                      title="Aggiungi evento giornata intera"
                    >
                      <FaPlus className="h-2 w-2" />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Resto del codice esistente per gli orari 7-23... */}
      {/* ...existing code per griglia oraria... */}
      <div
        ref={scrollYContainerRef} // ✅ ATTACCATO: questo è il contenitore che scorre verticalmente
        className="max-h-96 overflow-y-auto scroll-smooth"
        style={{ scrollBehavior: 'smooth' }}
      >
        <div className="relative">
          {orariGiorno.map((hour) => (
            <div key={hour} className={`grid grid-cols-8 gap-0 border-b last:border-b-0 ${cellBorder}`}>
              {/* Colonna orario */}
              <div className={`p-1 border-r ${cellBorder} ${headerBg} flex items-center justify-center min-h-[50px]`}>
                <span className={`text-sm font-medium ${textColor}`}>
                  {formatHour(hour)}
                </span>
                {hour >= 20 && (
                  <span className="ml-1 text-xs text-orange-500 dark:text-orange-400">🌙</span>
                )}
              </div>
              
              {/* Celle giornaliere */}
              {currentWeek.map((date) => {
                const dateKey = date.toDateString();
                const eventiInOrario = eventiPerGiornoOra[dateKey]?.[hour] || [];
                const cellKey = `${dateKey}-${hour}`;
                const isDropTarget = dragOverCell === cellKey && !weekNavigationTriggered;
                
                return (
                  <div
                    key={cellKey}
                    className={`
                      border-r last:border-r-0 ${cellBorder} min-h-[50px] 
                      transition-colors group relative
                      ${isDropTarget ? (theme === 'dark' ? 'bg-indigo-900/50' : 'bg-emerald-100/50') : ''}
                      ${isDragging ? 'ring-1 ring-dashed ring-gray-400 dark:ring-gray-500' : ''}
                    `}
                    onDragOver={(e) => handleDragOver(e, date, hour)}
                    onDragLeave={handleDragLeave} // ✅ ora esiste
                    onDrop={(e) => handleDrop(e, date, hour)}
                  >
                    <div 
                      className={`
                        p-1 h-full cursor-pointer 
                        ${!isDragging ? 'hover:bg-gray-50 dark:hover:bg-gray-700/30' : ''}
                        ${isDropTarget ? 'bg-blue-50 dark:bg-blue-900/30' : ''}
                      `}
                      onClick={(e) => {
                        if (e.target === e.currentTarget && !isDragging) {
                          handleCellClick(date, hour, false);
                        }
                      }}
                    >
                      {/* Eventi nell'orario */}
                      {eventiInOrario.map(evento => renderEvent(evento))}
                      
                      {/* Indicatore visivo drop zone */}
                      {isDropTarget && draggedEvent && (
                        <div className={`
                          absolute inset-1 border-2 border-dashed rounded-lg 
                          flex items-center justify-center z-30
                          ${theme === 'dark' ? 'border-indigo-400 bg-indigo-900/20' : 'border-emerald-400 bg-emerald-100/50'}
                        `}>
                          <div className={`text-xs font-medium ${theme === 'dark' ? 'text-indigo-300' : 'text-emerald-700'}`}>
                            📍 Rilascia qui
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Pulsante + */}
                    {!isDragging && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCellClick(date, hour, true);
                          }}
                          className={`
                            opacity-0 group-hover:opacity-100 
                            transition-all duration-200 
                            pointer-events-auto
                            ${theme === 'dark' ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-emerald-600 hover:bg-emerald-700'} 
                            text-white 
                            p-1.5 rounded-full 
                            shadow-lg hover:shadow-xl
                            transform hover:scale-110
                            z-20
                          `}
                          title={`Aggiungi evento - ${date.toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short' })} alle ${formatHour(hour)}`}
                        >
                          <FaPlus className="h-3 w-3" />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Footer con legend */}
      <div className={`${headerBg} border-t ${cellBorder} p-3`}>
        <div className="flex flex-wrap items-center gap-4 text-xs">
          <span className={`${mutedText} font-medium`}>Interazioni:</span>
          <div className="flex items-center space-x-1">
            <FaPlus className="h-3 w-3 text-emerald-600" />
            <span className={textColor}>Hover su cella per aggiungere evento</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-3 h-3 bg-blue-500 rounded"></div>
            <span className={textColor}>Click su evento per modificare</span>
          </div>
          <div className="flex items-center space-x-1">
            <span className="text-lg">🎯</span>
            <span className={textColor}>Trascina eventi per spostarli</span>
          </div>
          <div className="flex items-center space-x-1">
            <span className="text-lg">⬅️➡️</span>
            <span className={textColor}>Trascina ai bordi (primi 100px) per cambiare settimana</span>
          </div>
        </div>
      </div>

      {/* Legenda tipi evento */}
      <div className={`${headerBg} border-t ${cellBorder} p-3`}>
        <div className="flex flex-wrap items-center gap-4 text-xs">
          <span className={`${mutedText} font-medium`}>Tipi evento:</span>
          {Object.entries(EVENT_TYPE_COLORS).slice(0, 6).map(([tipo, config]) => (
            <div key={tipo} className="flex items-center space-x-1">
              <div className={`w-3 h-3 rounded ${config.bg}`}></div>
              <span className={textColor}>
                {config.descrizione || `Tipo ${tipo}`}
              </span>
            </div>
          ))}
          {Object.keys(EVENT_TYPE_COLORS).length > 6 && (
            <span className={`${mutedText} italic`}>
              +{Object.keys(EVENT_TYPE_COLORS).length - 6} altri...
            </span>
          )}
        </div>
      </div>

      {/* Indicatori visivi bordi durante drag - ✅ SEMPRE VISIBILI durante drag */}
      {isDragging && (
        <>
          <div className={`pointer-events-none absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-emerald-500/30 to-transparent flex items-center justify-start transition-opacity ${weekNavigationTriggered ? 'opacity-50' : 'opacity-100'}`}>
            <span className="ml-1 text-[10px] font-semibold text-emerald-700 dark:text-emerald-300 rotate-[-90deg]">
              PREV
            </span>
          </div>
          <div className={`pointer-events-none absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-emerald-500/30 to-transparent flex items-center justify-end transition-opacity ${weekNavigationTriggered ? 'opacity-50' : 'opacity-100'}`}>
            <span className="mr-1 text-[10px] font-semibold text-emerald-700 dark:text-emerald-300 rotate-90">
              NEXT
            </span>
          </div>
        </>
      )}
    </div>
  );
};

export default WeekView;